import type { OpeningHours } from "../api/types";

/**
 * Opening-hours arithmetic. Times are "HH:MM" strings in the café's local
 * time; a closing time past midnight ("01:00") belongs to the day it opened.
 */

export const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
export type DayKey = (typeof DAY_KEYS)[number];

/** JS getDay() is Sunday-first; the café's week starts on Monday. */
export function dayKeyOf(date: Date): DayKey {
  return DAY_KEYS[(date.getDay() + 6) % 7];
}

export function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

export function fromMinutes(total: number): string {
  const wrapped = ((total % 1440) + 1440) % 1440;
  const h = Math.floor(wrapped / 60);
  const m = wrapped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Closing minutes on the same axis as opening — past-midnight closes are > 1440. */
function closeMinutes(open: string, close: string): number {
  const o = toMinutes(open);
  const c = toMinutes(close);
  return c <= o ? c + 1440 : c;
}

export interface OpenState {
  isOpen: boolean;
  /** When open: today's closing time. When closed: next opening time. */
  time: string;
  /** When closed: 0 = later today, 1 = tomorrow, 2+ = in n days. */
  daysAhead: number;
  nextDay: DayKey;
}

export function openState(hours: OpeningHours, now = new Date()): OpenState {
  const minutesNow = now.getHours() * 60 + now.getMinutes();

  // Still inside yesterday's late closing?
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const yKey = dayKeyOf(yesterday);
  const yHours = hours[yKey];
  if (yHours) {
    const close = closeMinutes(yHours.open, yHours.close);
    if (close > 1440 && minutesNow < close - 1440) {
      return { isOpen: true, time: yHours.close, daysAhead: 0, nextDay: yKey };
    }
  }

  const todayKey = dayKeyOf(now);
  const today = hours[todayKey];
  if (today) {
    const open = toMinutes(today.open);
    const close = closeMinutes(today.open, today.close);
    if (minutesNow >= open && minutesNow < close) {
      return { isOpen: true, time: today.close, daysAhead: 0, nextDay: todayKey };
    }
    if (minutesNow < open) {
      return { isOpen: false, time: today.open, daysAhead: 0, nextDay: todayKey };
    }
  }

  for (let ahead = 1; ahead <= 7; ahead += 1) {
    const next = new Date(now);
    next.setDate(now.getDate() + ahead);
    const key = dayKeyOf(next);
    const h = hours[key];
    if (h) return { isOpen: false, time: h.open, daysAhead: ahead, nextDay: key };
  }
  return { isOpen: false, time: "", daysAhead: 7, nextDay: todayKey };
}

export interface SlotOptions {
  stepMinutes: number;
  /** Minutes before closing after which no slot is offered. */
  lastSlotBeforeClose: number;
  /** Earliest offered slot when the day is today, in minutes from now. */
  leadMinutes: number;
}

/** "HH:MM" slots for a calendar day, already trimmed to the future when it is today. */
export function slotsFor(
  hours: OpeningHours,
  date: Date,
  options: SlotOptions,
  now = new Date(),
): string[] {
  const day = hours[dayKeyOf(date)];
  if (!day) return [];

  const open = toMinutes(day.open);
  const close = closeMinutes(day.open, day.close) - options.lastSlotBeforeClose;

  const isToday = date.toDateString() === now.toDateString();
  const earliest = isToday
    ? Math.max(open, Math.ceil((now.getHours() * 60 + now.getMinutes() + options.leadMinutes) / options.stepMinutes) * options.stepMinutes)
    : open;

  const slots: string[] = [];
  for (let m = earliest; m <= close; m += options.stepMinutes) slots.push(fromMinutes(m));
  return slots;
}

/**
 * Is "HH:MM" inside the opening hours of that calendar day? A close past
 * midnight counts for the day it opened, so 00:30 on a Friday night is
 * Friday's 01:00 close, and the check runs on Friday's date.
 */
export function isWithinHours(hours: OpeningHours, date: Date, time: string): boolean {
  const day = hours[dayKeyOf(date)];
  if (!day || !/^\d{2}:\d{2}$/.test(time)) return false;
  const open = toMinutes(day.open);
  const close = closeMinutes(day.open, day.close);
  let t = toMinutes(time);
  if (t < open) t += 1440; // "00:30" on a night that closes at 01:00
  return t >= open && t < close;
}

/** A local "YYYY-MM-DD" for <input type="date">. Not toISOString: that is UTC. */
export function isoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function parseIsoDate(value: string): Date | null {
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}
